import React, { useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Row, Spinner, Pagination } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const PAGE_SIZE = 9;

const HrAndCrm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [workOrders, setWorkOrders] = useState([]);
  const [billings, setBillings] = useState([]);
  const [statusFilter, setStatusFilter] = usePersistentState("hr_crm_status", "All");
  const [projectFilter, setProjectFilter] = usePersistentState("hr_crm_project", searchParams.get("project") || "");
  const [workOrderFilter, setWorkOrderFilter] = usePersistentState("hr_crm_wo", searchParams.get("workOrder") || "");
  const [search, setSearch] = usePersistentState("hr_crm_search", "");
  const [currentPage, setCurrentPage] = usePersistentState("hr_crm_page", 1);
  const [loading, setLoading] = useState(true);

  /* =============================
     FETCH DATA
  ============================= */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [woRes, billingRes] = await Promise.all([
          api.get("/work-orders?limit=1000"),
          api.get("/billing?limit=1000"),
        ]);

        if (woRes.data?.success) {
          setWorkOrders(woRes.data.data || []);
        }
        if (billingRes.data?.success) {
          setBillings(billingRes.data.data || []);
        }
      } catch (err) {
        console.error("HR & CRM load error:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* =============================
     PLACEHOLDER STATUS CALCULATION
  ============================= */
  const workOrderHr = useMemo(() => {
    return workOrders.map((wo) => {
      const woValue = Number(wo.totalAmount || wo.value || 0);

      let raised = 0;
      billings.forEach((b) => {
        const woId =
          typeof b.workOrder === "object" ? b.workOrder?._id : b.workOrder;

        if (woId !== wo._id) return;

        const totalAmount = Number(b.totalAmount || 0);
        raised += totalAmount;
      });

      const percent = woValue
        ? Math.min(100, Math.round((raised / woValue) * 100))
        : 0;

      const label =
        raised >= woValue && woValue > 0
          ? "Completed"
          : raised > 0
          ? "In Progress"
          : "Pending";

      return {
        workOrder: wo._id,
        info: wo,
        total: woValue,
        completed: raised,
        percent,
        label,
      };
    });
  }, [workOrders, billings]);

  /* =============================
     DERIVED PROJECTS & AVAILABLE WORK ORDERS
  ============================= */
  const projects = useMemo(() => {
    const map = {};
    workOrders.forEach((w) => {
      const pId = w.project?._id || (typeof w.project === "string" ? w.project : null);
      const pName = w.project?.projectName || w.projectName || w.project?.name;
      if (pId && pName) {
        map[pId] = pName;
      }
    });
    return Object.entries(map);
  }, [workOrders]);

  const availableWorkOrders = useMemo(() => {
    if (!projectFilter) return workOrders;
    return workOrders.filter((w) => {
      const pId = w.project?._id || w.project;
      return String(pId) === String(projectFilter);
    });
  }, [workOrders, projectFilter]);

  /* =============================
     FILTER ACROSS ALL WORK ORDERS
  ============================= */
  const filteredWorkOrders = useMemo(() => {
    return workOrderHr.filter((wo) => {
      if (statusFilter !== "All" && wo.label !== statusFilter) return false;

      if (projectFilter) {
        const pId = wo.info?.project?._id || (typeof wo.info?.project === "string" ? wo.info.project : null);
        if (String(pId) !== String(projectFilter)) return false;
      }

      if (workOrderFilter && String(wo.workOrder) !== String(workOrderFilter)) return false;

      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const woNo = String(wo.info?.woNo || wo.info?.woNumber || "").toLowerCase();
        const label = String(wo.info?.label || "").toLowerCase();
        const projectName = String(wo.info?.project?.projectName || wo.info?.projectName || "").toLowerCase();
        const projectCode = String(wo.info?.project?.projectCode || "").toLowerCase();
        const clientName = String(wo.info?.client?.companyName || wo.info?.client?.name || "").toLowerCase();

        const matches =
          woNo.includes(q) ||
          label.includes(q) ||
          projectName.includes(q) ||
          projectCode.includes(q) ||
          clientName.includes(q);

        if (!matches) return false;
      }

      return true;
    });
  }, [workOrderHr, statusFilter, projectFilter, workOrderFilter, search]);

  /* =============================
     PAGINATION
  ============================= */
  const totalCount = filteredWorkOrders.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const paginatedWorkOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredWorkOrders.slice(start, start + PAGE_SIZE);
  }, [filteredWorkOrders, currentPage]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [];

    const delta = 2;
    const start = Math.max(2, currentPage - delta);
    const end   = Math.min(totalPages - 1, currentPage + delta);
    const items = [];

    items.push(
      <Pagination.Item key={1} active={currentPage === 1} onClick={() => setCurrentPage(1)}>
        1
      </Pagination.Item>
    );

    if (start > 2) items.push(<Pagination.Ellipsis key="s-ellipsis" disabled />);

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={currentPage === p} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="e-ellipsis" disabled />);

    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  }, [currentPage, totalPages]);

  const handleResetAllFilters = () => {
    try {
      sessionStorage.removeItem("hr_crm_status");
      sessionStorage.removeItem("hr_crm_project");
      sessionStorage.removeItem("hr_crm_wo");
      sessionStorage.removeItem("hr_crm_search");
      sessionStorage.removeItem("hr_crm_page");
    } catch (_) {}
    setProjectFilter("");
    setWorkOrderFilter("");
    setStatusFilter("All");
    setSearch("");
    setCurrentPage(1);
  };

  /* =============================
     HELPERS
  ============================= */
  const statusVariant = (label) =>
    label === "Completed" ? "success" : label === "In Progress" ? "warning" : "danger";

  /* =============================
     UI
  ============================= */
  return (
    <>
      <div className="mb-3">
        <PageTitle activeMenu="HR & CRM" motherMenu="Commercial" />
      </div>

      {/* ===== FILTER + ACTION BUTTONS IN EXACTLY ONE ROW ===== */}
      <div className="d-flex align-items-end justify-content-between gap-2 mb-4">
        {/* Left side: Filter Controls */}
        <div className="d-flex align-items-end gap-2 flex-grow-1" style={{ minWidth: 0 }}>
          {/* Search */}
          <div style={{ flex: "1 1 140px", minWidth: "110px" }}>
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              autoComplete="off"
            />
          </div>

          {/* Project */}
          <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
            <Form.Label className="small fw-semibold mb-1">Project</Form.Label>
            <Form.Select
              value={projectFilter}
              onChange={(e) => {
                setProjectFilter(e.target.value);
                setWorkOrderFilter("");
                setCurrentPage(1);
              }}
            >
              <option value="">All Projects</option>
              {projects.map(([pId, pName]) => (
                <option key={pId} value={pId}>
                  {pName}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Work Order */}
          <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
            <Form.Label className="small fw-semibold mb-1">Work Order</Form.Label>
            <Form.Select
              value={workOrderFilter}
              onChange={(e) => {
                setWorkOrderFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Work Orders</option>
              {availableWorkOrders.map((w) => (
                <option key={w._id} value={w._id}>
                  {w.label ? `${w.label} (${w.woNo || w.woNumber})` : (w.woNo || w.woNumber || "WO")}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* HR / CRM Status */}
          <div style={{ width: "135px", flexShrink: 0 }}>
            <Form.Label className="small fw-semibold mb-1">HR / CRM Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All">All</option>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
              <option value="Pending">Pending</option>
            </Form.Select>
          </div>

          {/* Reset Filters */}
          {(projectFilter || workOrderFilter || statusFilter !== "All" || search) && (
            <div style={{ flexShrink: 0 }}>
              <Button
                variant="outline-secondary"
                className="btn-input-height d-flex align-items-center justify-content-center shadow-sm"
                style={{ borderRadius: "1rem", width: "3rem", minWidth: "3rem" }}
                title="Reset Filters"
                onClick={handleResetAllFilters}
              >
                <i className="fas fa-undo"></i>
              </Button>
            </div>
          )}
        </div>

        {/* Right side: Action Buttons (height & styling matching filter boxes) */}
        <div className="d-flex gap-2 align-items-center flex-shrink-0">
          <Button
            variant="outline-success"
            className="btn-input-height shadow-sm text-nowrap"
            style={{ borderRadius: "1rem", fontSize: "0.875rem", fontWeight: 600, padding: "0 16px" }}
            onClick={() => navigate("/staff-reimbursements")}
          >
            Reimbursements
          </Button>
          <Button
            variant="primary"
            className="btn-input-height shadow-sm text-nowrap"
            style={{ borderRadius: "1rem", fontSize: "0.875rem", fontWeight: 600, padding: "0 16px" }}
            onClick={() => navigate("/staff-salary")}
          >
            Attendance & Salary
          </Button>
          <Button
            variant="primary"
            className="btn-input-height shadow-sm text-nowrap"
            style={{ borderRadius: "1rem", fontSize: "0.875rem", fontWeight: 600, padding: "0 16px" }}
            onClick={() => navigate("/admin-task")}
          >
            All Tasks
          </Button>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          {totalCount > 0 && (
            <div className="text-muted small mb-3">
              Showing{" "}
              <strong>
                {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, totalCount)}
              </strong>{" "}
              of <strong>{totalCount}</strong> work orders
            </div>
          )}

          {paginatedWorkOrders.length === 0 ? (
            <div className="text-center text-muted py-5">
              No work orders found for the selected filter.
            </div>
          ) : (
            <Row className="g-3">
              {paginatedWorkOrders.map((wo) => {
                const info = wo.info;
                if (!info) return null;

                const percent = wo.percent;
                const label = wo.label;

                return (
                  <Col key={wo.workOrder} md={6} xl={4}>
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        {/* HEADER */}
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <h6 className="fw-bold mb-1 text-primary" style={{ lineHeight: 1.2 }}>
                              {info?.label || info?.woNo || info?.woNumber || "Work Order"}
                            </h6>
                            <div className="text-muted small mb-1" style={{ fontWeight: 600 }}>
                              <i className="fas fa-sitemap me-1 opacity-60"></i>
                              {info?.woNo || info?.woNumber || "—"}
                            </div>
                            <div className="text-dark fw-medium small mb-1">
                              <i className="fas fa-project-diagram me-1 text-muted"></i>
                              {info?.project?.projectName || "—"}
                              {info?.project?.projectCode && (
                                <span className="text-muted ms-1">({info.project.projectCode})</span>
                              )}
                            </div>
                            {(info?.client?.companyName || info?.client?.name) && (
                              <div className="text-muted small mb-1">
                                <i className="fas fa-building me-1 opacity-75"></i>
                                {info.client?.companyName || info.client?.name}
                              </div>
                            )}
                          </div>

                          <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                            {info?.client && (
                              <div
                                title={info.client?.companyName || info.client?.name || "Client"}
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: "8px",
                                  border: "1px solid #e9ecef",
                                  background: "#f8f9fa",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                {info.client?.image ? (
                                  <img
                                    src={getFileUrl(info.client.image)}
                                    alt=""
                                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: 2 }}
                                  />
                                ) : (
                                  <i className="fas fa-building text-muted"></i>
                                )}
                              </div>
                            )}
                            <Badge bg={statusVariant(label)}>{label}</Badge>
                          </div>
                        </div>

                        {/* ===== PROGRESS ===== */}
                        <div className="d-flex justify-content-center mt-3">
                          <div
                            className="circular-progress"
                            style={{
                              "--value": percent,
                              "--progress-color":
                                label === "Completed"
                                  ? "#198754"
                                  : label === "In Progress"
                                  ? "#ffc107"
                                  : "#dc3545",
                            }}
                          >
                            <span>{percent}%</span>
                          </div>
                        </div>

                        {/* ===== HR & CRM ACTIONS ===== */}
                        <div className="mt-3">
                          <div className="small fw-semibold text-uppercase text-muted mb-1">
                            HR & CRM
                          </div>

                          <div className="d-flex gap-2 flex-wrap">
                            {/* Tasks — blue */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ border: "1px solid #0d6efd", color: "#0d6efd", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0d6efd"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#0d6efd"; }}
                              onClick={() => navigate(`/admin-task?workOrder=${wo.workOrder}`)}
                            >
                              Tasks
                            </Button>

                            {/* Employee CRM — purple */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ border: "1px solid #6f42c1", color: "#6f42c1", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#6f42c1"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#6f42c1"; }}
                              onClick={() => navigate(`/employee-crm?workOrder=${wo.workOrder}`)}
                            >
                              Employee CRM
                            </Button>

                            {/* Payroll Settings — orange */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ border: "1px solid #fd7e14", color: "#fd7e14", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fd7e14"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#fd7e14"; }}
                              onClick={() => navigate(`/payroll-settings?workOrder=${wo.workOrder}`)}
                            >
                              Payroll Settings
                            </Button>

                            {/* Attendance — green */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ border: "1px solid #198754", color: "#198754", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#198754"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#198754"; }}
                              onClick={() => navigate(`/attendance?workOrder=${wo.workOrder}`)}
                            >
                              Attendance
                            </Button>

                            {/* Salary Register — pink */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ border: "1px solid #e83e8c", color: "#e83e8c", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e83e8c"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#e83e8c"; }}
                              onClick={() => navigate(`/staff-salary`)}
                            >
                              Staff Salary
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* ===== PAGINATION ===== */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                />
                {paginationItems}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                />
              </Pagination>
              <span className="text-muted small">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default HrAndCrm;
